import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from "../../environments/environment"
import { UtilService } from '@app/shared/services/util.service';
import { OperationService } from '@app/operation/operationanalysis/operationanalysis.service';
import { ApiService } from '@app/shared/services/api.service';
import { CommentService } from '@app/operation/comments-modal/comment.service';

@Component({
    imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule, RouterModule],
    selector: 'app-view-ticket',
    templateUrl: './view-ticket.component.html',
    styleUrls: ['./view-ticket.component.css']
})
export class ViewTicketComponent implements OnInit, OnDestroy {
  ticket: any;
  newComment = '';
  currentUserName: string = '';
  showComments = false;
  hasConversations = false;
  pollingInterval: any; 
  sanitizedDescription: SafeHtml | null = null;
  tableName1: string = 'Ticket ';
  tableName: string = 'View Ticket ';
  Home:string = 'Home ';
  ticketMentionableUsers: any[] = [];
  
  showPdfModal = false;
  currentPdfFile: any = null;
  pdfViewerUrl: SafeResourceUrl | null = null;
  
  modalImageUrl: string | null = null;
  showImageModal = false;
  isMediaPlaying: boolean = false;

  constructor(
    private route: ActivatedRoute, 
    private http: HttpClient, 
    private cdr: ChangeDetectorRef, 
    private sanitizer: DomSanitizer, 
    private routes: Router,
    private util: UtilService,
    private operationService: OperationService,
    private api: ApiService,
    private commentService: CommentService
  ) { }

  ngOnInit(): void {
    const token = localStorage.getItem('JwtToken');
    if (!token) {
      const returnUrl = (this.routes.url && this.routes.url !== '/' && !this.routes.url.includes('/account/')) ? this.routes.url : (window.location.pathname + window.location.search);
      this.routes.navigate(['/account/login'], { queryParams: { returnUrl } });
      return;
    }

    this.currentUserName = localStorage.getItem('username') || 'You';
    this.ticket = history.state?.ticketData || {
      id: 0,
      status: 'Unknown',
      priority: 'Unknown',
      description: 'No description available',
      detail: 'No details available',
      comments: [],
      attachments: []
    };
    
    if (this.ticket && this.ticket.description) {
      const prepared = this.prepareHtmlForRender(this.ticket.description);
      this.sanitizedDescription = this.sanitizer.bypassSecurityTrustHtml(prepared);
    }

    if (this.ticket?.comments?.length > 0 && this.ticket.comments[0].body_text) {
      this.ticket.comments = this.ticket.comments.map((comment: any) => ({
        author: comment.user_id ? 'Admin' : this.currentUserName,
        text: comment.body_text,
        timestamp: comment.updated_at || comment.created_at || '',
        isAdmin: !!comment.user_id
      }));
    }
    
    this.showComments = true;
    this.loadTicketMentionableUsers();
    this.fetchTicket();
    this.fetchComments();
    this.startPolling();
    // Setup video event listeners after a short delay to allow DOM to render
    setTimeout(() => this.attachVideoEventListeners(), 500);
  }

  ngOnDestroy(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    this.removeVideoEventListeners();
  }

  // Attach event listeners to video elements
  private attachVideoEventListeners(): void {
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(video => {
      video.addEventListener('play', () => this.onVideoPlay());
      video.addEventListener('pause', () => this.onVideoPause());
      video.addEventListener('ended', () => this.onVideoEnded());
    });
  }

  // Remove event listeners from video elements
  private removeVideoEventListeners(): void {
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(video => {
      video.removeEventListener('play', () => this.onVideoPlay());
      video.removeEventListener('pause', () => this.onVideoPause());
      video.removeEventListener('ended', () => this.onVideoEnded());
    });
  }

  // Called when video starts playing
  private onVideoPlay(): void {
    this.isMediaPlaying = true;
  }

  // Called when video is paused
  private onVideoPause(): void {
    this.isMediaPlaying = false;
  }

  // Called when video ends
  private onVideoEnded(): void {
    this.isMediaPlaying = false;
  }

  startPolling(): void {
    this.pollingInterval = setInterval(() => {
      // Don't fetch if media is playing
      if (!this.isMediaPlaying) {
        this.fetchTicket();
        this.fetchComments();
      }
    }, 10000);
  }

  navigatetab(): void {
    this.routes.navigate(['/ticket']);
  }

  home(): void {
    this.routes.navigate(['/'])
  }

  toggleComments(): void {
    if (!this.showComments && this.hasConversations) {
      this.showComments = true;
      this.fetchComments();
    }
  }

  private getFreshdeskHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + btoa(environment.FRESHDESK_API_KEY + ':X'),
      'Accept': 'application/json'
    });
  }

  fetchTicket(): void {
    const ticketId = this.ticket.id;
    if (!ticketId) return;
  
    this.api.get(`/tickets/${ticketId}`).subscribe(
      (ticketData: any) => {
        const resBody = ticketData?.body !== undefined ? ticketData.body : ticketData;
        const ticket = resBody?.responseBody || resBody;
        if (ticket) {
          this.applyTicketData(ticket);
        }
      },
      error => {
        // Fall back to Freshdesk for legacy tickets
        this.http.get(`${environment.FRESHDESK_API}/${ticketId}`, { headers: this.getFreshdeskHeaders() }).subscribe(
          (ticket: any) => {
            if (ticket) {
              this.applyTicketData(ticket);
            }
          },
          fErr => console.warn('Could not fetch ticket details from Freshdesk either:', fErr)
        );
      }
    );
  }

  private applyTicketData(ticket: any): void {
    this.ticket.status = typeof ticket.status === 'number' ? this.getStatusText(ticket.status) : (ticket.status || this.ticket.status);
    this.ticket.priority = typeof ticket.priority === 'number' ? this.getPriorityText(ticket.priority) : (ticket.priority || this.ticket.priority);
    this.ticket.description = ticket.description || ticket.description_text || '';
    
    const preparedHtml = this.prepareHtmlForRender(this.ticket.description || '');
    this.sanitizedDescription = this.sanitizer.bypassSecurityTrustHtml(preparedHtml);
    
    this.ticket.summary = ticket.subject || this.ticket.summary;
    this.ticket.createdDate = ticket.created_at || this.ticket.createdDate;
    this.ticket.detail = ticket.subject || this.ticket.detail;
    
    if (ticket.attachments && ticket.attachments.length > 0) {
      const desc = this.ticket.description || '';
      this.ticket.attachments = ticket.attachments.filter((att: any) => {
        if (att.content_type?.startsWith('image/') && att.attachment_url) {
          const baseUrl = att.attachment_url.split('?')[0];
          return !desc.includes(baseUrl);
        }
        return true;
      });
    }

    this.cdr.detectChanges();
  }

  // Prepare HTML for safe rendering while preserving formatting
  private prepareHtmlForRender(html: string): string {
    if (!html) return html;
    try {
      let processed = html;
      
      // Ensure margin-left and text-align styles are properly preserved
      // Don't wrap elements that already have margin-left (indentation) or text-align (alignment)
      processed = processed
        .replace(/<p([^>]*)>/g, (match, attrs) => {
          if (/style="[^"]*(?:margin-left|text-align)/.test(match)) {
            return match; // Keep as is
          }
          if (/style="/.test(match)) {
            return match.replace(/style="/, 'style="line-height: 1.6;');
          }
          return '<p style="margin: 0.5em 0; line-height: 1.6;">';
        })
        .replace(/<div([^>]*)>/g, (match, attrs) => {
          if (/style="[^"]*(?:margin-left|text-align)/.test(match)) {
            return match;
          }
          if (/style="/.test(match)) {
            return match.replace(/style="/, 'style="line-height: 1.6;');
          }
          return '<div style="margin: 0.5em 0; line-height: 1.6;">';
        })
        .replace(/<li([^>]*)>/g, (match, attrs) => {
          if (/style="[^"]*(?:margin-left|text-align)/.test(match)) {
            return match;
          }
          if (/style="/.test(match)) {
            return match.replace(/style="/, 'style="line-height: 1.6;');
          }
          return '<li style="margin: 0.25em 0; line-height: 1.6;">';
        });

      // Now process images with lazy loading
      const imgProcessed = processed.replace(/<img(\s+[^>]*)?>/gi, (match) => {
        if (/loading\s*=/.test(match)) {
          if (/style\s*=/.test(match)) {
            return match.replace(/style\s*=\s*"([^"]*)"/i, (m, styles) => {
              if (/max-width/.test(styles)) return m;
              return `style="${styles};max-width:100%;height:auto;"`;
            });
          }
          return match.replace(/<img/i, '<img style="max-width:100%;height:auto;"');
        }
        if (/style\s*=/.test(match)) {
          return match.replace(/<img/i, '<img loading="lazy"');
        }
        return match.replace(/<img/i, '<img loading="lazy" style="max-width:100%;height:auto;"');
      });

      return imgProcessed;
    } catch (e) {
      return html;
    }
  }
  
  private getOrgId(): string {
    if (this.ticket?.org_id) return String(this.ticket.org_id);
    if (this.ticket?.orgId) return String(this.ticket.orgId);
    const orgIdEnc = localStorage.getItem('OrgId');
    if (orgIdEnc) {
      const orgId = this.util.decrypt(orgIdEnc);
      if (orgId) return String(orgId);
    }
    return '-1';
  }

  fetchComments(): void {
    if (!this.ticket || !this.ticket.id) return;
  
    const ticketId = this.ticket.id;
    const orgId = this.getOrgId();
  
    this.commentService.getComments('ticket', String(ticketId), orgId).subscribe({
      next: (response: any) => {
        const list = response?.body?.data || response?.data || (Array.isArray(response) ? response : []);
        if (Array.isArray(list) && list.length > 0) {
          this.applyCassandraComments(list);
        } else {
          this.fetchFreshdeskComments(ticketId);
        }
      },
      error: () => {
        this.fetchFreshdeskComments(ticketId);
      }
    });
  }

  private fetchFreshdeskComments(ticketId: any): void {
    this.http.get<any[]>(`${environment.FRESHDESK_API}/${ticketId}/conversations`, { headers: this.getFreshdeskHeaders() }).subscribe(
      (convList: any[]) => {
        if (Array.isArray(convList) && convList.length > 0) {
          this.applyComments(convList);
        }
      },
      err => console.warn('Could not fetch Freshdesk comments:', err)
    );
  }

  private applyCassandraComments(rawList: any[]): void {
    const flatList: any[] = [];
    const flatten = (items: any[]) => {
      for (const item of items) {
        const author = item.created_by || 'User';
        const isFromRequester = author.toLowerCase() === this.currentUserName.toLowerCase();
        flatList.push({
          author: author,
          text: item.comment_text ? item.comment_text.replace(/<[^>]*>/g, '') : (item.body_text || item.body || ''),
          timestamp: item.created_at,
          isAdmin: !isFromRequester
        });
        if (Array.isArray(item.replies) && item.replies.length > 0) {
          flatten(item.replies);
        }
      }
    };
    flatten(rawList);
    flatList.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    this.ticket.comments = flatList;
    this.cdr.detectChanges();
  }

  private applyComments(convList: any[]): void {
    this.ticket.comments = convList
      .filter((conv: any) => (conv.body_text || conv.body) && !conv.private)
      .map((conv: any) => {
        const body = conv.body || '';
        const userName = this.currentUserName.toLowerCase();

        const isFromRequester = (conv.author_name && conv.author_name.toLowerCase() === userName) ||
          new RegExp(`<b>\\s*${userName}\\s*:\\s*</b>`, 'i').test(body);

        return {
          author: conv.author_name || (isFromRequester ? this.currentUserName : 'Support Team'),
          text: conv.body_text || conv.body || '',
          timestamp: conv.updated_at || conv.created_at,
          isAdmin: conv.is_admin ?? !isFromRequester
        };
      });

    this.cdr.detectChanges();
  }

  // Image modal methods
  openImageModal(url: string): void {
    this.modalImageUrl = url;
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.modalImageUrl = null;
  }

  getPriorityText(priority: number): string {
    const priorityMap: { [key: number]: string } = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Urgent',
    };
    return priorityMap[priority] || 'Unknown';
  }

  getStatusText(status: number): string {
    switch (status) {
      case 2: return 'Open';
      case 3: return 'Pending';
      case 4: return 'Resolved';
      case 5: return 'Closed';
      default: return 'Status ' + status;
    }
  }

  addComment(): void {
    if (this.newComment.trim()) {
      const ticketId = this.ticket.id;
      const commentText = this.newComment.trim();
      const mentionRecipients = this.extractMentionRecipients(commentText);
      const orgId = this.getOrgId();

      const optimisticComment = {
        author: this.currentUserName,
        text: commentText,
        timestamp: new Date().toISOString(),
        isAdmin: false
      };

      this.ticket.comments.push(optimisticComment);
      this.newComment = '';

      this.commentService.addComment(
        'ticket',
        String(ticketId),
        orgId,
        commentText,
        this.currentUserName,
        this.ticket.status || 'open'
      ).subscribe({
        next: async (response) => {
          console.log('Comment added to Cassandra successfully:', response);
          this.hasConversations = true;
          if (mentionRecipients.length > 0) {
            try {
              await this.sendTicketMentionNotifications(mentionRecipients, ticketId, commentText);
            } catch (mentionError) {
              console.error('Ticket mention notification failed:', mentionError);
            }
          }
          setTimeout(() => this.fetchComments(), 500);
        },
        error: (error) => {
          console.error('Error adding comment to Cassandra:', error);
          this.ticket.comments.pop();
        }
      });
    }
  }

  getNonMediaAttachments(): any[] {
    if (!this.ticket?.attachments?.length) return [];
    
    return this.ticket.attachments.filter((att: any) => {
      const contentType = att.content_type?.toLowerCase() || '';
      const fileName = att.name?.toLowerCase() || '';
      
      // Check if it's NOT image or video
      const isImage = contentType.startsWith('image/');
      const isVideo = contentType.startsWith('video/');
      
      // Check if it's a document file
      const isPdf = contentType.includes('pdf') || fileName.endsWith('.pdf');
      const isExcel = contentType.includes('excel') || 
                      contentType.includes('spreadsheet') || 
                      fileName.endsWith('.xls') || 
                      fileName.endsWith('.xlsx');
      const isWord = contentType.includes('word') || 
                     contentType.includes('document') || 
                     fileName.endsWith('.doc') || 
                     fileName.endsWith('.docx');
      const isText = contentType.includes('text/') || 
                     fileName.endsWith('.txt');
      const isCSV = fileName.endsWith('.csv');
      const isPowerPoint = contentType.includes('presentation') || 
                           fileName.endsWith('.ppt') || 
                           fileName.endsWith('.pptx');
      
      return !isImage && !isVideo && (isPdf || isExcel || isWord || isText || isCSV || isPowerPoint);
    });
  }

  getFileIcon(file: any): string {
    const contentType = file.content_type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    
    if (contentType.includes('pdf') || fileName.endsWith('.pdf')) {
      return 'picture_as_pdf';
    } else if (contentType.includes('excel') || 
               contentType.includes('spreadsheet') || 
               fileName.endsWith('.xls') || 
               fileName.endsWith('.xlsx')) {
      return 'table_chart';
    } else if (contentType.includes('word') || 
               contentType.includes('document') || 
               fileName.endsWith('.doc') || 
               fileName.endsWith('.docx')) {
      return 'description';
    } else if (contentType.includes('text/') || fileName.endsWith('.txt')) {
      return 'text_snippet';
    } else if (fileName.endsWith('.csv')) {
      return 'grid_on';
    } else if (contentType.includes('presentation') || 
               fileName.endsWith('.ppt') || 
               fileName.endsWith('.pptx')) {
      return 'slideshow';
    } else {
      return 'insert_drive_file';
    }
  }

  getFileIconClass(file: any): string {
    const contentType = file.content_type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    
    if (contentType.includes('pdf') || fileName.endsWith('.pdf')) {
      return 'pdf-icon';
    } else if (contentType.includes('excel') || 
               contentType.includes('spreadsheet') || 
               fileName.endsWith('.xls') || 
               fileName.endsWith('.xlsx')) {
      return 'excel-icon';
    } else if (contentType.includes('word') || 
               contentType.includes('document') || 
               fileName.endsWith('.doc') || 
               fileName.endsWith('.docx')) {
      return 'word-icon';
    } else if (contentType.includes('text/') || fileName.endsWith('.txt')) {
      return 'text-icon';
    } else if (fileName.endsWith('.csv')) {
      return 'csv-icon';
    } else if (contentType.includes('presentation') || 
               fileName.endsWith('.ppt') || 
               fileName.endsWith('.pptx')) {
      return 'ppt-icon';
    } else {
      return 'generic-icon';
    }
  }

  getFileType(file: any): string {
    const contentType = file.content_type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    
    if (contentType.includes('pdf') || fileName.endsWith('.pdf')) {
      return 'PDF Document';
    } else if (contentType.includes('excel') || 
               contentType.includes('spreadsheet') || 
               fileName.endsWith('.xls') || 
               fileName.endsWith('.xlsx')) {
      return 'Excel Spreadsheet';
    } else if (contentType.includes('word') || 
               contentType.includes('document') || 
               fileName.endsWith('.doc') || 
               fileName.endsWith('.docx')) {
      return 'Word Document';
    } else if (contentType.includes('text/') || fileName.endsWith('.txt')) {
      return 'Text File';
    } else if (fileName.endsWith('.csv')) {
      return 'CSV File';
    } else if (contentType.includes('presentation') || 
               fileName.endsWith('.ppt') || 
               fileName.endsWith('.pptx')) {
      return 'PowerPoint Presentation';
    } else {
      return 'File';
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Check if file is PDF
  isPdfFile(file: any): boolean {
    const contentType = file.content_type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    return contentType.includes('pdf') || fileName.endsWith('.pdf');
  }

  // PDF preview open karein
  previewPdf(file: any): void {
    this.currentPdfFile = file;
    this.pdfViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.attachment_url);
    this.showPdfModal = true;
  }

  // PDF preview close karein
  closePdfModal(): void {
    this.showPdfModal = false;
    this.currentPdfFile = null;
    this.pdfViewerUrl = null;
  }

  // Check if file is Excel
  isExcelFile(file: any): boolean {
    const contentType = file.content_type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    return contentType.includes('excel') || 
           contentType.includes('spreadsheet') || 
           fileName.endsWith('.xls') || 
           fileName.endsWith('.xlsx');
  }

  // Check if file is Word document
  isWordFile(file: any): boolean {
    const contentType = file.content_type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    return contentType.includes('word') || 
           contentType.includes('document') || 
           fileName.endsWith('.doc') || 
           fileName.endsWith('.docx');
  }

  private getOrganizationIdFromStorage(): string {
    const orgIdEnc = localStorage.getItem('OrgId');
    if (!orgIdEnc) return '';

    try {
      const decrypted = this.util.decrypt(orgIdEnc);
      return decrypted || orgIdEnc;
    } catch {
      return orgIdEnc;
    }
  }

  private normalizeTicketUsers(users: any[]): any[] {
    return (users || [])
      .map((user) => ({
        ...user,
        id: user.id ?? user.user_id,
        full_name: user.full_name || user.name || '',
        username: user.username || '',
        email: user.email || '',
      }))
      .filter((user) => !!user.id && !!user.email);
  }

  private loadTicketMentionableUsers(): void {
    const organizationId = this.getOrganizationIdFromStorage();
    if (!organizationId) {
      this.ticketMentionableUsers = [];
      return;
    }

    this.operationService.getStakeholders({ org_id: organizationId }).subscribe({
      next: (res: any) => {
        const rawUsers = res?.body?.responseBody || res?.body || [];
        this.ticketMentionableUsers = this.normalizeTicketUsers(Array.isArray(rawUsers) ? rawUsers : []);
      },
      error: (err) => {
        console.error('Error loading ticket mentionable users:', err);
        this.ticketMentionableUsers = [];
      }
    });
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private extractMentionRecipients(commentText: string): any[] {
    const comment = commentText || '';
    const normalizedComment = comment.toLowerCase();
    const currentEmail = (localStorage.getItem('useremail') || '').trim().toLowerCase();
    const recipients = new Map<string, any>();
    const emailPattern = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;

    for (const user of this.ticketMentionableUsers) {
      const mentionNames = [user.username, user.full_name]
        .filter((value) => !!value)
        .map((value) => String(value).trim())
        .filter((value) => !!value)
        .sort((a, b) => b.length - a.length);

      const emailValue = String(user.email || '').trim().toLowerCase();
      const nameMatched = mentionNames.some((identifier) => {
        const pattern = new RegExp(`(^|\\s)@${this.escapeRegExp(identifier.toLowerCase())}(?=$|\\s|[.,;:!?])`, 'i');
        return pattern.test(normalizedComment);
      });
      const emailMatched = emailValue
        ? new RegExp(`(^|[^\\w.-])${this.escapeRegExp(emailValue)}(?=$|[^\\w.-])`, 'i').test(normalizedComment)
        : false;

      if ((nameMatched || emailMatched) && user.email && user.email.toLowerCase() !== currentEmail) {
        recipients.set(String(user.id), user);
      }
    }

    const directEmails = comment.match(emailPattern) || [];
    for (const email of directEmails) {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!normalizedEmail || normalizedEmail === currentEmail) {
        continue;
      }

      const existingUser = this.ticketMentionableUsers.find(
        (user) => String(user.email || '').trim().toLowerCase() === normalizedEmail
      );

      if (existingUser) {
        recipients.set(String(existingUser.id), existingUser);
        continue;
      }

      recipients.set(`email:${normalizedEmail}`, {
        email: normalizedEmail,
        full_name: '',
        username: '',
      });
    }

    return Array.from(recipients.values());
  }

  private async sendTicketMentionNotifications(mentionRecipients: any[], ticketId: number, commentText: string): Promise<void> {
    if (!mentionRecipients.length) {
      return;
    }

    const payload = {
      requestBody: {
        recipients: mentionRecipients.map((recipient) => ({
          email: recipient.email,
          full_name: recipient.full_name || recipient.username || recipient.email,
          username: recipient.username || '',
        })),
        subject: `You were mentioned in ticket #${ticketId}`,
        comment: this.escapeHtml(commentText).replace(/\n/g, '<br>'),
        commenter_name: this.currentUserName,
        ticket_id: ticketId,
        ticket_subject: this.ticket?.summary || this.ticket?.detail || '',
        ticket_status: this.ticket?.status || '',
        ticket_priority: this.ticket?.priority || '',
        ticket_url: window.location.href,
        org_name: this.ticket?.orgName || '',
        org_id: this.getOrgId() || this.ticket?.org_id || null,
      }
    };

    await this.api.post('/sendTicketMentionEmail', payload).toPromise();
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
